import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Search, CheckCircle2, XCircle, Loader2, ShoppingCart } from 'lucide-react';
import {
  UniversalPage,
  Card,
  Input,
  Button,
  StatusBadge,
  NotificationCard,
  MoneyCell,
  FormField,
} from '@sighthost/ui';
import { siteConfig } from '@sighthost/config';
import { whoisService, type WhoisResult } from '@/domains/domains-registry';

/**
 * TLD coverage verified live against the domains.co.za reseller API on
 * 2026-07-02 (AD-23 .za-only restriction lifted for the verified set) —
 * pricing rows live in `system_setting.domains_pricing`. Unpriced verified
 * TLDs (online/store/xyz/io) stay off this dropdown — concierge via support.
 *
 * 2026-09-25 (domain gap analysis G5): this page carried its OWN price table
 * — a fifth copy the "four copies" rule in CLAUDE.md §4.9 did not know
 * about — and it still showed the below-cost prices retired on 2026-09-13
 * (.com R199 against an invoice of R269; .capetown R149 against R349). It
 * now reads `siteConfig.domainSearch.tlds`, the same list the marketing
 * site and NewServicePage read. The authoritative price is still
 * `domain-register-quote` at quote-time.
 */
const TLD_OPTIONS: ReadonlyArray<{ value: string; priceZAR: number }> = siteConfig.domainSearch.tlds
  .filter((t): t is { tld: string; priceZar: number; selfServe: true } => t.selfServe && t.priceZar !== null)
  .map((t) => ({ value: `.${t.tld}`, priceZAR: t.priceZar }));

/**
 * The upsell fan-out checks the chosen TLD plus these evergreen alternates —
 * NOT the whole dropdown. The availability EF rate-limits 60 checks/IP/hour;
 * fanning out all 11 options would burn the budget in 5 searches.
 */
const UPSELL_TLDS = ['.co.za', '.com', '.africa'] as const;

const SLD_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export default function RegisterDomainPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sld, setSld] = useState('');
  const [tld, setTld] = useState<string>('.co.za');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [results, setResults] = useState<
    | { fqdn: string; price: number; result: WhoisResult }[]
    | null
  >(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Marketing DomainSearch deep-link (2026-07-02): /app/domains/register?domain=name.co.za
  // pre-fills the searched name and re-runs the lookup so the visitor lands on
  // their result, not an empty form.
  const autoSearched = useRef(false);
  useEffect(() => {
    if (autoSearched.current) return;
    const fromUrl = searchParams.get('domain')?.trim().toLowerCase();
    if (!fromUrl) return;
    autoSearched.current = true;
    const match = TLD_OPTIONS.find((opt) => fromUrl.endsWith(opt.value));
    const cleaned = match ? fromUrl.slice(0, -match.value.length) : fromUrl.split('.')[0] ?? '';
    if (!SLD_RE.test(cleaned)) return;
    setSld(cleaned);
    if (match) setTld(match.value);
    void runSearch(cleaned, match?.value ?? tld);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only deep-link handoff
  }, []);

  async function onSearch(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setValidationError(null);
    setLookupError(null);

    const cleaned = sld.trim().toLowerCase();
    if (!SLD_RE.test(cleaned)) {
      setValidationError(
        'Use letters, digits, and hyphens (max 63 chars). Hyphens cannot start or end the name.',
      );
      return;
    }

    await runSearch(cleaned, tld);
  }

  async function runSearch(cleaned: string, chosenTld: string) {
    setSubmitting(true);
    setResults(null);
    try {
      // Look up the chosen TLD plus a short evergreen alternate list.
      const lookupTlds = [...new Set([chosenTld, ...UPSELL_TLDS])];
      const options = TLD_OPTIONS.filter((opt) => lookupTlds.includes(opt.value));
      const lookups = await Promise.all(
        options.map(async (opt) => {
          const fqdn = `${cleaned}${opt.value}`;
          try {
            const result = await whoisService.lookup(fqdn);
            return { fqdn, price: opt.priceZAR, result };
          } catch {
            return null;
          }
        }),
      );
      const filtered = lookups.filter((x): x is NonNullable<typeof x> => x !== null);
      // Place the user's chosen TLD first so it's the primary visual answer.
      filtered.sort((a, b) => {
        if (a.fqdn.endsWith(chosenTld)) return -1;
        if (b.fqdn.endsWith(chosenTld)) return 1;
        return 0;
      });
      setResults(filtered);
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Lookup failed — try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <UniversalPage
      title="Register a domain"
      description="Search availability across .za, .com, .africa, city TLDs and more."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Domains', href: '/app/domains' },
        { label: 'Register' },
      ]}
    >
      <Card interactive={false}>
        <form onSubmit={onSearch} className="p-6 space-y-4">
          <FormField
            label="Search availability"
            htmlFor="sld"
            helper="Type the name only (no http://, no .co.za) — the TLD is below."
            error={validationError ?? undefined}
          >
            <Input
              id="sld"
              value={sld}
              onChange={(e) => setSld(e.target.value)}
              prefix="https://"
              suffix={
                <select
                  aria-label="TLD"
                  value={tld}
                  onChange={(e) => setTld(e.target.value)}
                  disabled={submitting}
                  className="bg-transparent text-sm tabular-nums text-foreground-soft focus:outline-none"
                >
                  {TLD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.value}
                    </option>
                  ))}
                </select>
              }
              placeholder="mybusiness"
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              disabled={submitting}
              error={Boolean(validationError)}
            />
          </FormField>

          <div className="flex justify-end">
            <Button type="submit" loading={submitting} disabled={submitting}>
              {submitting ? null : <Search className="size-4" />}
              {submitting ? 'Checking…' : 'Check availability'}
            </Button>
          </div>
        </form>
      </Card>

      {lookupError && <NotificationCard tone="danger" title="Lookup failed" body={lookupError} />}

      {results && results.length > 0 && (
        <Card interactive={false}>
          <header className="flex items-center justify-between border-b border-border px-6 py-3">
            <h2 className="text-sm font-semibold text-ink">Search results</h2>
            <span className="text-xs text-muted-foreground">
              {results.length} TLD{results.length === 1 ? '' : 's'} checked
            </span>
          </header>
          <ul className="divide-y divide-border">
            {results.map((row) => (
              <li
                key={row.fqdn}
                className="flex flex-wrap items-center gap-3 px-6 py-4"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {row.result.available ? (
                    <CheckCircle2 className="size-5 shrink-0 text-success" aria-hidden />
                  ) : (
                    <XCircle className="size-5 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                  <span className="truncate text-sm tabular-nums text-ink">
                    {row.fqdn}
                  </span>
                  <StatusBadge tone={row.result.available ? 'active' : 'draft'}>
                    {row.result.available ? 'Available' : 'Taken'}
                  </StatusBadge>
                </div>
                <div className="flex items-center gap-3">
                  <MoneyCell value={row.price} />
                  <span className="text-xs text-muted-foreground">/ year</span>
                  <Button
                    size="sm"
                    variant={row.result.available ? 'primary' : 'outline'}
                    disabled={!row.result.available}
                    onClick={() =>
                      row.result.available &&
                      navigate(`/app/domains/register/${encodeURIComponent(row.fqdn)}`)
                    }
                  >
                    <ShoppingCart className="size-3.5" />
                    {row.result.available ? 'Register' : 'Unavailable'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <footer className="border-t border-border bg-card-subtle px-6 py-3 text-xs text-muted-foreground">
            Click <strong>Register</strong> on an available name to provide registrant details and
            create the invoice. The domain registers automatically once payment clears.
          </footer>
        </Card>
      )}

      {results && results.length === 0 && (
        <Card interactive={false} className="p-8 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto mb-2 size-5 animate-spin text-muted-foreground" aria-hidden />
          Couldn't reach the registry. Try again in a moment.
        </Card>
      )}
    </UniversalPage>
  );
}
